import { useState, useEffect, useMemo } from 'react';
import {
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  FormHelperText,
  CircularProgress,
} from '@material-ui/core';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder';
import { CustomFieldExtensionSchema } from '@backstage/plugin-scaffolder-react';
import jsonpath from 'jsonpath';
import useDebounce from 'react-use/lib/useDebounce';
import { DevHubCheckbox } from '@devxdiscover/devhub-ui';
import { ConfigHelper, isRelativeUrl } from '@devxdiscover/devhub-common';
import { md5Hash } from '../../util/md5.mjs';
import { cacheGet, cacheSet } from '../../util/persistedState';
import { UIOptionsType } from '@rjsf/utils';

/**
 * Options for the ApiMultiSelect component
 */
export type ApiMultiSelectOptions = UIOptionsType & {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'HEAD';
  headers?: Record<string, string> | { name?: string; value?: string }[];
  body?: string;
  valueJsonPath: string;
  cacheTtlSeconds?: number;
  sortResults?: 'None' | 'Ascending' | 'Descending'; // Added 'None'
  label?: string;
};

/**
 * Schema for the ApiMultiSelect field extension
 */
export const ApiMultiSelectSchema: CustomFieldExtensionSchema = {
  uiOptions: {
    type: 'object',
    properties: {
      label: {
        title: 'Label',
        type: 'string',
        description: 'The label to display for the field',
      },
      url: {
        title: 'URL',
        type: 'string',
        description: 'The URL to fetch the options from',
      },
      cacheTtlSeconds: {
        title: 'Cache TTL (seconds)',
        type: 'number',
        description:
          'The number of seconds to cache the options for (default: 300)',
        default: 300,
      },
      sortResults: {
        title: 'Sort Results',
        enum: ['None', 'Ascending', 'Descending'],
        default: 'None',
        description: 'The sort order of the results',
      },
      method: {
        title: 'Method',
        type: 'string',
        enum: ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'HEAD'],
        default: 'GET',
        description: 'The HTTP method to use when fetching the options',
      },
      body: {
        title: 'Body',
        type: 'string',
        description: 'The body to send with the request',
      },
      headers: {
        title: 'Headers',
        type: 'array',
        items: {
          type: 'object',
          properties: {
            name: {
              title: 'Name',
              type: 'string',
            },
            value: {
              title: 'Value',
              type: 'string',
            },
          },
        },
      },
      valueJsonPath: {
        title: 'Value JSONPath',
        type: 'string',
        description:
          'The JSONPath to use to extract the value from the response',
      },
    },
  },
  returnValue: {
    type: 'array',
    items: { type: 'string' },
  },
};

/**
 * Normalize headers from array or object to Record<string, string>
 * @param headers - Headers in array or object format
 * @returns Normalized headers as Record<string, string>
 */
const normalizeHeaders = (
  headers?: Record<string, string> | { name?: string; value?: string }[],
): Record<string, string> => {
  if (Array.isArray(headers)) {
    return headers.reduce(
      (acc, header) => {
        if (header?.name) {
          acc[header.name] = header.value ?? '';
        }
        return acc;
      },
      {} as Record<string, string>,
    );
  }
  return headers ?? {};
};

const fetchOptions = async (
  options: ApiMultiSelectOptions,
  controller: AbortController,
  valuePath: string,
  formData?: Record<string, any>,
): Promise<string[]> => {
  const { url, method = 'GET', headers } = options;

  let requestBody = options.body;
  if (requestBody && formData) {
    requestBody = requestBody.replace(
      /\{\{(\w+)}}/g,
      (_match, variableName) => formData[variableName] ?? _match,
    );
  }

  const normalizedHeaders = normalizeHeaders(headers);

  if (!isRelativeUrl(url)) {
    throw new Error(
      `ApiMultiSelect options.url must be a relative URL; received: ${url}`,
    );
  }
  const resolvedUrl = `${ConfigHelper.instance.backendUrl}${url}`;

  const cacheKey = md5Hash(
    JSON.stringify({
      url,
      method,
      headers: normalizedHeaders,
      body: requestBody,
      valuePath,
      sortResults: options.sortResults,
    }),
  );
  const cacheResult = cacheGet<string[]>(cacheKey);

  if (cacheResult) {
    return cacheResult;
  }

  let data: unknown;
  try {
    const upperMethod = method.toUpperCase();
    const hasBody =
      requestBody !== undefined &&
      upperMethod !== 'GET' &&
      upperMethod !== 'HEAD';

    const fetchHeaders: Record<string, string> = {
      ...normalizedHeaders,
    };

    if (
      hasBody &&
      !Object.keys(fetchHeaders).some(
        headerName => headerName.toLowerCase() === 'content-type',
      )
    ) {
      fetchHeaders['Content-Type'] = 'application/json';
    }

    const fetchInit: RequestInit = {
      method,
      headers: fetchHeaders,
      signal: controller.signal,
    };

    if (hasBody) {
      fetchInit.body = requestBody;
    }

    const response = await fetch(resolvedUrl, fetchInit);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch options from ${resolvedUrl} with status ${response.status}`,
      );
    }

    data = await response.json();

    // Filter/coerce results to string[]
    const stringResults = jsonpath
      .query(data, valuePath)
      .map(value => String(value));

    // Always dedupe results in O(n) time
    const dedupedResults = Array.from(new Set(stringResults));

    switch (options.sortResults) {
      case 'Ascending':
        dedupedResults.sort((a, b) => a.localeCompare(b));
        break;
      case 'Descending':
        dedupedResults.sort((a, b) => b.localeCompare(a));
        break;
      default:
        break;
    }

    cacheSet(cacheKey, dedupedResults, {
      seconds: options.cacheTtlSeconds || 300,
    });
    return dedupedResults;
  } catch (err) {
    const originalMessage = err instanceof Error ? err.message : String(err);
    throw new Error(
      `Error in fetchOptions for ${method} ${resolvedUrl} and valuePath "${valuePath}": ${originalMessage}`,
    );
  }
};

/**
 * A scaffolder field extension component that fetches options from an API
 * and renders them as checkboxes for multi-selection.
 * @param props - The field extension component props
 * @returns The ApiMultiSelect component
 */
export const ApiMultiSelectComponent = (
  props: FieldExtensionComponentProps<string[], ApiMultiSelectOptions>,
) => {
  const {
    required,
    uiSchema,
    rawErrors,
    idSchema,
    onChange,
    formData = [],
    formContext = {},
  } = props;

  const opts = uiSchema['ui:options'] ?? ({} as ApiMultiSelectOptions);
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState<string[]>([]);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [valuePath, setValuePath] = useState<string | undefined>(undefined);

  useDebounce(
    () => {
      if (opts.valueJsonPath && formContext?.formData) {
        const interpolated = opts.valueJsonPath.replace(
          /\{\{(\w+)}}/g,
          (_match, variableName) =>
            formContext.formData[variableName] ?? _match,
        );
        setValuePath(interpolated);
      } else {
        setValuePath(opts.valueJsonPath);
      }
    },
    500,
    [opts.valueJsonPath, formContext?.formData],
  );

  const formDataHash = useMemo(
    () => md5Hash(JSON.stringify(formContext?.formData ?? {})),
    [formContext?.formData],
  );

  // Hash for all relevant opts fields to avoid stale options
  const optsHash = useMemo(
    () =>
      md5Hash(
        JSON.stringify({
          url: opts.url,
          method: opts.method,
          headers: opts.headers,
          body: opts.body,
          sortResults: opts.sortResults,
          cacheTtlSeconds: opts.cacheTtlSeconds,
        }),
      ),
    [
      opts.url,
      opts.method,
      opts.headers,
      opts.body,
      opts.sortResults,
      opts.cacheTtlSeconds,
    ],
  );

  useEffect(() => {
    if (!opts.url) {
      setLoading(false);
      setOptions([]);
      setFetchError(null);
      if (formData.length > 0) {
        onChange([]);
      }
      return undefined;
    }
    if (!valuePath || valuePath.includes('{{')) {
      setLoading(true);
      return undefined;
    }

    setLoading(true);
    setFetchError(null);
    let isCancelled = false;
    const controller = new AbortController();

    (async () => {
      try {
        const fetchedOptions = await fetchOptions(
          opts,
          controller,
          valuePath,
          formContext?.formData,
        );
        if (isCancelled) {
          return;
        }
        setOptions(fetchedOptions);

        if (formData.length > 0) {
          const validSelections = formData.filter(value =>
            fetchedOptions.includes(value),
          );
          if (validSelections.length !== formData.length) {
            onChange(validSelections);
          }
        }
      } catch (error: unknown) {
        if (controller.signal.aborted || isCancelled) {
          return;
        }
        setOptions([]);
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        setFetchError(`Failed to load options: ${errorMessage}`);
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isCancelled = true;
      controller.abort();
    };
    // Deps use hashes (optsHash, formDataHash) instead of raw objects (opts, formContext)
    // to prevent unnecessary refetches when object references change but content is the same
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optsHash, valuePath, formDataHash, formData.length, onChange]);

  const handleChange = (value: string, checked: boolean) => {
    if (checked) {
      onChange([...formData, value]);
    } else {
      onChange(formData.filter(v => v !== value));
    }
  };

  return (
    <FormControl
      required={required}
      error={rawErrors?.length > 0}
      component="fieldset"
      fullWidth
      margin="dense"
    >
      <FormLabel component="legend" id={`${idSchema?.$id}-label`}>
        {opts.label || 'Select Options'}
        {required && ' *'}
      </FormLabel>
      {loading ? (
        <CircularProgress size={24} style={{ margin: '16px 0' }} />
      ) : (
        <FormGroup>
          {options.map(option => (
            <FormControlLabel
              key={option}
              control={
                <DevHubCheckbox
                  checked={formData.includes(option)}
                  onChange={e => handleChange(option, e.target.checked)}
                  name={option}
                />
              }
              label={option}
            />
          ))}
        </FormGroup>
      )}
      {(rawErrors?.length > 0 || fetchError) && (
        <FormHelperText id={`${idSchema?.$id}-helper`} error>
          {[...(rawErrors ?? []), fetchError].filter(Boolean).join(', ')}
        </FormHelperText>
      )}
    </FormControl>
  );
};
